import React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'

import { useAlert } from 'components/Shared/Alert'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import participantAPI from 'api/ApiActions/Participant/Participant'

type UpgradeToProjectAdminModalProps = {
  isOpen: boolean
  setModalVisibility: (isOpen: boolean) => void
  context: any
}

const UpgradeToProjectAdminModal = ({
  isOpen,
  setModalVisibility,
  context,
}: UpgradeToProjectAdminModalProps) => {
  const { t } = useTranslation()
  const { showAlert } = useAlert()
  const theme: AdminUITheme = useTheme()
  const { functions, state }: any = React.useContext(context)
  const { participantSummary } = state
  const { getParticipant } = functions

  const { id, login } = participantSummary

  const handleClose = () => {
    setModalVisibility(false)
  }

  const handleError = () => {
    showAlert({ type: 'error', title: t('alert.error.400') })
  }

  const handleSuccess = () => {
    showAlert({
      type: 'success',
      title: t('participant.makeProjectAdmin.successMessage', {
        name: login,
      }),
    })
  }

  const handleClick = async () => {
    const result = await participantAPI.makeProjectAdmin({ userId: id })

    const { code, status } = result

    if (status === 204) {
      handleSuccess()
      await getParticipant()
      handleClose()
    } else if (code === 'ERR_BAD_REQUEST') {
      handleError()
      handleClose()
    }
  }

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogContent sx={theme.Participant.makeProjectAdminContent}>
        <Stack spacing={3}>
          <Typography variant="h5" component="h5" sx={{ pb: 1 }}>
            {t('participant.makeProjectAdmin.upgradeToProjectAdmin', {
              name: login,
            })}
          </Typography>
          <Typography
            variant="body1"
            component="h5"
            dangerouslySetInnerHTML={{
              __html: t('participant.makeProjectAdmin.description'),
            }}
          />
          <Typography variant="body1" component="h5">
            {t('participant.makeProjectAdmin.confirmation', {
              name: login,
            })}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={theme.Participant.makeProjectAdminContentActions}>
        <Button
          sx={theme.Participant.makeProjectAdminDialogCancelBtnMob}
          variant="outlined"
          onClick={handleClose}
        >
          {t('common.no')}
        </Button>
        <Button
          sx={theme.Participant.makeProjectAdminDialogCancelBtnDesk}
          variant="text"
          onClick={handleClose}
        >
          {t('common.no')}
        </Button>
        <Button
          variant="contained"
          onClick={handleClick}
          data-testid="make-project-admin-yes"
        >
          {t('common.yes')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UpgradeToProjectAdminModal
